'use client';

import { useCallback, useState } from 'react';
import { cartService } from 'services/api/client';
import { logger } from 'utils';
import { useCartStore } from 'stores/cart';

interface UseAddToCartReturn {
  addToCart: (productId: number) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook: useAddToCart
 *
 * This hook provides functionality to add products to the shopping cart with
 * loading state management, error handling, and automatic cart count updates.
 *
 * Key functionalities:
 * - Adds a product to the cart using the `addProductToCart` service.
 * - Manages loading state during the add-to-cart operation.
 * - Updates the cart overview and main article count after successful addition.
 * - Handles and logs errors during the operation using the `logger` utility.
 *
 * Returns:
 * - addToCart: An async function that accepts a productId and adds it to the cart.
 * - isLoading: A boolean indicating whether the add-to-cart operation is in progress.
 * - error: An Error object if the operation failed, or null if successful/idle.
 *
 */
export const useAddToCart = (): UseAddToCartReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { setMainArticleCount } = useCartStore();

  const addToCart = useCallback(
    async (productId: number) => {
      setIsLoading(true);
      setError(null);

      try {
        await cartService.addProductToCart(productId);

        // Refresh cart overview to get updated count
        const cartOverview = await cartService.getCartOverview(1);
        setMainArticleCount(cartOverview.totalMainArticleCount);
      } catch (err) {
        const error = err as Error;
        const unknownErrorString = 'UnknownError';

        setError(error);

        logger.error({
          ns: 'add_to_cart',
          type: error.name || unknownErrorString,
          severity: 'high',
          msg: error.message || unknownErrorString,
          loggedFrom: 'useAddToCart',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setMainArticleCount],
  );

  return { addToCart, isLoading, error };
};
